import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { AlertCircle, Camera, Loader2, Upload, X, User, Shield } from 'lucide-react';
import { API, useAdminAuth } from '../context/AdminAuthContext';
import { useToast } from '../hooks/use-toast';
import { formatDateTime } from '../lib/dateFormat';
import { orderPartyPrimary, orderPartyContact } from '../lib/orderParty';

/**
 * LoadingPicturesModal — Feb 2026 admin panel.
 *
 * Opens off the "Loading Pictures" action on the AdminOrders row.
 * Two responsibilities:
 *   1. Render every proof-of-load photo the order already has,
 *      grouped by the loader they're credited to (`attributed_to`).
 *      Photos uploaded by the loader themselves and photos an admin
 *      uploaded on their behalf sit in the same section — the "via
 *      admin" badge disambiguates when the uploader was different.
 *   2. Let the admin upload one or more new photos on behalf of a
 *      loader picked from the order's loading-task assignees.
 *
 * All storage flows through the existing loading-photos pipeline
 * (Google Drive primary, inline base64 fallback), so no downstream
 * reader needs a code change to see admin-uploaded photos.
 */
const LoadingPicturesModal = ({ order, onClose, onUploaded }) => {
  const { authHeaders } = useAdminAuth();
  const { toast } = useToast();
  const [assignees, setAssignees] = useState([]);
  const [assigneesLoading, setAssigneesLoading] = useState(true);
  const [photos, setPhotos] = useState([]);
  const [photosLoading, setPhotosLoading] = useState(true);
  const [selectedEmail, setSelectedEmail] = useState('');
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  const orderId = order?.id || '';

  const loadAssignees = async () => {
    if (!orderId) return;
    try {
      const r = await axios.get(
        `${API}/admin/orders/${orderId}/loading-assignees`,
        { headers: authHeaders },
      );
      const list = r.data?.assignees || [];
      setAssignees(list);
      // Default to the first loader (if any) so a common workflow
      // — an admin uploading photos for the sole loader — is one
      // fewer click.
      if (list.length > 0 && !selectedEmail) {
        setSelectedEmail(list[0].email);
      }
    } catch (_e) {
      toast({ title: 'Could not load assignees', description: 'Try again.' });
    } finally {
      setAssigneesLoading(false);
    }
  };

  const loadPhotos = async () => {
    if (!orderId) return;
    try {
      const r = await axios.get(
        `${API}/admin/orders/${orderId}/loading-photos`,
        { headers: authHeaders },
      );
      setPhotos(r.data?.photos || []);
    } catch (_e) {
      // Silent — modal still functional without existing photos.
    } finally {
      setPhotosLoading(false);
    }
  };

  useEffect(() => {
    loadAssignees();
    loadPhotos();
  }, [orderId]);

  // Group photos by attributed_to for the display below the upload
  // panel. Preserves the fetch order (uploaded_at ascending) within
  // each user's section.
  const groupedPhotos = useMemo(() => {
    const groups = new Map();
    for (const p of photos) {
      const key = p.attributed_to || p.uploaded_by || 'unknown';
      if (!groups.has(key)) {
        groups.set(key, {
          email: key,
          name: p.attributed_to_name || key.split('@')[0] || 'Unknown',
          photos: [],
        });
      }
      groups.get(key).photos.push(p);
    }
    return Array.from(groups.values());
  }, [photos]);

  const handleUpload = async () => {
    if (!selectedEmail) {
      toast({ title: 'Select a loader first', description: 'Pick who the photos should be credited to.' });
      return;
    }
    if (!files.length) {
      toast({ title: 'No files chosen', description: 'Add at least one photo before uploading.' });
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('on_behalf_of_email', selectedEmail);
      for (const f of files) fd.append('files', f);
      const r = await axios.post(
        `${API}/admin/orders/${orderId}/loading-photos/admin-upload`,
        fd,
        { headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' } },
      );
      toast({
        title: 'Uploaded',
        description: `${r.data?.count || 0} photo${(r.data?.count || 0) === 1 ? '' : 's'} attributed to ${selectedEmail}.`,
      });
      setFiles([]);
      await loadPhotos();
      if (onUploaded) onUploaded();
    } catch (err) {
      toast({
        title: 'Upload failed',
        description: err.response?.data?.detail || 'Try again.',
      });
    } finally {
      setUploading(false);
    }
  };

  if (!order) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-2 sm:p-6"
      data-testid="loading-pictures-modal"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-3xl max-h-[92vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-start justify-between gap-3 px-5 py-4 border-b border-neutral-200">
          <div>
            <div className="flex items-center gap-2 text-[#0F172A] font-bold text-lg">
              <Camera className="w-5 h-5 text-[#0078D4]" />
              Loading Pictures
            </div>
            <div className="text-xs text-neutral-500 mt-0.5">
              {order.order_number} · {orderPartyPrimary(order)}
              {orderPartyContact(order) && (
                <span className="text-neutral-500"> ({orderPartyContact(order)})</span>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-neutral-400 hover:text-neutral-900"
            data-testid="loading-pictures-close"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-6">
          {/* Upload panel */}
          <section
            className="rounded-xl border border-[#0078D4]/30 bg-[#0078D4]/5 p-4 space-y-3"
            data-testid="loading-pictures-upload-panel"
          >
            <div className="text-sm font-bold text-[#0F172A] flex items-center gap-1.5">
              <Upload className="w-4 h-4" /> Upload on behalf of a loader
            </div>
            {/* Feb 2026 fix — inline explanation when the dropdown is
                empty. Silently disabling the Upload button left admins
                guessing why the click did nothing. */}
            {!assigneesLoading && assignees.length === 0 && (
              <div
                className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900"
                data-testid="loading-pictures-no-loader-hint"
                role="alert"
              >
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5 text-amber-600" />
                <div>
                  <div className="font-bold">No loader assigned to this order yet</div>
                  <div className="mt-0.5">
                    Open the WMS Tasks panel on the order and pick a loader
                    for the <span className="font-semibold">Loading</span> stage,
                    then reopen this modal to upload photos on their behalf.
                  </div>
                </div>
              </div>
            )}
            <div>
              <label
                className="block text-[10px] uppercase tracking-wider text-neutral-500 font-bold mb-1"
                htmlFor="loading-pictures-assignee"
              >
                Loader
              </label>
              <select
                id="loading-pictures-assignee"
                value={selectedEmail}
                onChange={(e) => setSelectedEmail(e.target.value)}
                disabled={assigneesLoading}
                className="w-full h-10 rounded-lg border border-neutral-300 px-3 text-sm bg-white disabled:opacity-60"
                data-testid="loading-pictures-assignee-select"
              >
                <option value="">
                  {assigneesLoading
                    ? 'Loading assignees…'
                    : assignees.length === 0
                      ? 'No loaders assigned to this order yet'
                      : 'Select a loader…'}
                </option>
                {assignees.map((a) => (
                  <option
                    key={a.email}
                    value={a.email}
                    data-testid={`loading-pictures-assignee-option-${a.email}`}
                  >
                    {a.name} ({a.email}){a.source === 'intent' ? ' · pre-assigned' : ''}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label
                className="block text-[10px] uppercase tracking-wider text-neutral-500 font-bold mb-1"
                htmlFor="loading-pictures-files"
              >
                Photos
              </label>
              <input
                id="loading-pictures-files"
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => setFiles(Array.from(e.target.files || []))}
                className="block w-full text-sm text-neutral-700 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-[#0078D4]/10 file:text-[#0078D4] hover:file:bg-[#0078D4]/20"
                data-testid="loading-pictures-file-input"
              />
              {files.length > 0 && (
                <div className="mt-1.5 text-xs text-neutral-500">
                  {files.length} file{files.length === 1 ? '' : 's'} chosen
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={handleUpload}
              disabled={uploading || !selectedEmail || files.length === 0}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-bold bg-[#0078D4] hover:bg-[#005A9E] text-white transition disabled:opacity-60"
              data-testid="loading-pictures-upload-btn"
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {uploading ? 'Uploading…' : 'Upload'}
            </button>
          </section>

          {/* Existing photos, grouped by user */}
          <section data-testid="loading-pictures-existing">
            <div className="text-sm font-bold text-[#0F172A] mb-2">Existing loading photos</div>
            {photosLoading ? (
              <div className="py-6 flex justify-center">
                <Loader2 className="w-5 h-5 animate-spin text-neutral-400" />
              </div>
            ) : groupedPhotos.length === 0 ? (
              <div
                className="rounded-xl border border-dashed border-neutral-300 px-4 py-8 text-center text-sm text-neutral-500"
                data-testid="loading-pictures-empty"
              >
                No loading pictures uploaded for this order yet.
              </div>
            ) : (
              groupedPhotos.map((group) => (
                <div
                  key={group.email}
                  className="mb-4 last:mb-0"
                  data-testid={`loading-pictures-group-${group.email}`}
                >
                  <div className="flex items-center gap-2 text-xs font-bold text-neutral-700 mb-2">
                    <User className="w-3.5 h-3.5" />
                    {group.name}
                    <span className="text-neutral-400 font-normal">· {group.email}</span>
                    <span className="ml-auto text-neutral-500 font-semibold">
                      {group.photos.length} photo{group.photos.length === 1 ? '' : 's'}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {group.photos.map((p) => (
                      <a
                        key={p.file_id}
                        href={`${API}/admin/orders/${orderId}/loading-photos/${p.file_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="relative rounded-lg overflow-hidden border border-neutral-200 hover:border-[#0078D4] transition group"
                        data-testid={`loading-pictures-thumb-${p.file_id}`}
                      >
                        <img
                          src={`${API}/admin/orders/${orderId}/loading-photos/${p.file_id}`}
                          alt={p.name}
                          className="w-full aspect-square object-cover"
                        />
                        {p.uploader_role === 'admin_on_behalf' && (
                          <span
                            className="absolute top-1 left-1 inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800"
                            title={`Uploaded by ${p.uploaded_by_name || p.uploaded_by} on behalf of ${p.attributed_to_name}`}
                            data-testid={`loading-pictures-via-admin-${p.file_id}`}
                          >
                            <Shield className="w-2.5 h-2.5" /> via admin
                          </span>
                        )}
                        <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[10px] px-1.5 py-1 opacity-0 group-hover:opacity-100 transition">
                          {formatDateTime(p.uploaded_at)}
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              ))
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default LoadingPicturesModal;
